import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

type Variant = "primary" | "secondary" | "danger" | "tertiary";

interface AuthButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: Variant;
  loading?: boolean;
  /** Label shown while `loading` is true. */
  loadingLabel?: string;
  /** Leading icon (replaced by spinner during loading). */
  leadingIcon?: ReactNode;
  /** `type` defaults to `button` — pass `submit` explicitly for form CTAs. */
  type?: "button" | "submit" | "reset";
  /** True to render at full container width. */
  fullWidth?: boolean;
  children: ReactNode;
}

/**
 * AuthButton — composes the dashboard's `.adp-btn-*` classes:
 *   primary  → Maritime Blue brand fill (the commit action)
 *   secondary → foreground outline (nav / read-only CTA)
 *   danger    → red outline (destructive)
 *   tertiary  → plain text link, never a button surface (cancel / "use other method")
 *
 * Press feedback is class-driven (opacity on primary, surface-shift on
 * secondary). No transform: scale, no shadow lift — institutional press
 * affordance only.
 */
export const AuthButton = forwardRef<HTMLButtonElement, AuthButtonProps>(function AuthButton(
  {
    variant = "primary",
    loading = false,
    loadingLabel,
    leadingIcon,
    type = "button",
    fullWidth = false,
    disabled,
    children,
    className = "",
    ...rest
  },
  ref,
) {
  const classes = [
    variant === "tertiary" ? "auth-tertiary" : "adp-btn",
    variant === "primary" ? "adp-btn-primary" : "",
    variant === "secondary" ? "adp-btn-secondary" : "",
    variant === "danger" ? "adp-btn-danger" : "",
    fullWidth ? "w-full" : "",
    "inline-flex items-center justify-center gap-2",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin motion-reduce:hidden" aria-hidden="true" />
      ) : (
        leadingIcon
      )}
      <span>{loading && loadingLabel ? loadingLabel : children}</span>
    </button>
  );
});
